import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import {
  countTotalVariants,
  MAX_MANUFACTURING_METHODS,
  MAX_MATERIALS_PER_METHOD,
  MAX_TOTAL_VARIANTS,
  AUTO_ORIENT_SERVICE,
  AutoOrient,
} from '@infinitform/platform-core';
import { environment } from '../config/editor-environment';
import { ManufacturingMethod, ProjectType, RunType } from '../models/enums';
import { DEFAULT_MOLD_CONFIG, DEFAULT_SOFT_JAWS_CONFIG, Project } from '../models/interfaces';
import { LoadCaseItem } from '../models/loadcase.model';
import { Material } from '../models/material.model';
import { ProgressService } from './progress.service';
import { normalizeToNewShape } from './project-normalization.util';
import {
  captureActiveCaseFromFlat,
  projectFlatFromCase,
  computeCaseLabel,
  makeDefaultCase,
  canAddMethod,
  FlatManufacturingFields,
  normalizeToolSizeUnit,
  supportsCastingParametric,
} from './case-sync.util';
import { makeDefaultVariant, buildVariantLabelMap, computeVariantLabel, resolveModelMaterialId } from './variant.util';
import { MODEL_INPUT_FRAME } from '../utils/local-frame.util';
import { storeInputsInModelFrame } from '../utils/model-alignment.util';

/** Outcome of {@link ProjectConfigurationService.renameVariant}. */
export type RenameVariantResult =
  | { status: 'renamed' }
  | { status: 'unchanged' }
  | { status: 'unknown-variant' }
  /** Another variant in this project already carries this name. */
  | { status: 'duplicate'; name: string };

/** Presigned geometry URLs go stale, so case detail is only briefly cached. */
const CASE_DETAIL_TTL_MS = 5 * 60 * 1000;

/** Per-case detail handed to the assistant (see fetchMasterCaseDetails). */
export interface ManufacturingCaseDetail {
  id: string;
  methodId: string | null;
  label: string | null;
  status: number | null;
  material: { name: string | null; density: number | null; pricePerKg: number | null } | null;
  geometry: { organic: string | null; prismatic: string | null };
}

@Injectable({
  providedIn: 'root'
})
export class ProjectConfigurationService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private progressService = inject(ProgressService);
  private readonly autoOrientService = inject(AUTO_ORIENT_SERVICE, { optional: true });

  public project_id?: string | null;
  public other_user_id?: string | null;

  // Example project state
  public isExampleProject = false;
  public copyingExampleProject = false;

  public projectImages: {
    image?: string;
    image_optimized?: string;
    image_optimized_parametric?: string;
    image_fea_stress?: string;
    image_fea_displacement?: string;
  } = {};

  public project: Project = {
    name: '',
    materials: [],
    status: 1,
    projectType: ProjectType.TopologyOptimization,
    optimization: undefined,
    parts: {
      design: [],
      nondesign: []
    },
    partGroups: [],
    loadcases: [],
    goal: {
      type: 'Generative Design Exploration',
      objective: { type: 'Min', responseId: 'compliance-response' },
      fractionOfMaterial: 30,
      massTarget: 0,
      frequencyConstraint: 200,
      constraints: [],
      responses: []
    },
    resolution: 'Low',
    symmetry: { xy: false, xz: false, yz: false },
    manufacturingMethod: ManufacturingMethod.AdditiveManufacturing,
    millingVector: { xp: false, yp: false, zp: false, xm: false, ym: false, zm: false },
    drawDirectionVector: { xp: false, yp: false, zp: false, xm: false, ym: false, zm: false },
    ribsDensity: 'Low',
    minimumFeatureSize: 0,
    minimumToolSize: 0,
    minimumToolSizeUnit: 'mm',
    wallThickness: 10,
    bottomCutFrac: 0.6,
    userCoordinateSystem: {
      origin: [0, 0, 0],
      rotation: [0, 0, 0],
      enabled: false,
      inputFrame: MODEL_INPUT_FRAME
    },
    coordinateSystems: [],
    generateOrganic: true,
    generatePrismatic: false,
    skeletonMethod: 'Skeleton3D',
    stressConstraintEnabled: true,
    deformationConstraintEnabled: false,
    flowVersion: 1,
    runType: RunType.Standard,
    softJaws: DEFAULT_SOFT_JAWS_CONFIG,
    mold: DEFAULT_MOLD_CONFIG,
    userDefinedResolution: { enabled: false, value: 600000 },
    solverParameters: {
      static: { tolerance: 0.0001, maxIterations: 150 },
      eigenvalue: { tolerance: 0.0001, maxIterations: 150 },
    }
  };

  public downloadLinks: any;
  /** Persisted BDF state from findOneForGUI; null on masters / projects without BDF. */
  public bdfJob: {
    status: string;
    s3Key?: string;
    localPath?: string;
    filename?: string;
    source?: 'organic' | 'prismatic' | 'analysis';
    progress?: number;
    stage?: string;
  } | null = null;
  public softJawsResultUrl?: string;

  public sharing: any;
  public isViewOnlyPermission = () => this.sharing?.permission === 0;
  public isEditablePermission = () => this.sharing?.permission === 1 || !this.sharing;
  public isSoftJawsProject = () => this.project.projectType === ProjectType.SoftJaws;
  public isMoldProject = () => this.project.projectType === ProjectType.Mold;

  private projectLoaded = false;
  private cachedProjectData: any = null;
  /** Which project cachedProjectData actually belongs to. */
  private cachedProjectId?: string | null = null;

  setProject(project: any): void {
    try {
      if (!project) {
        throw new Error('Invalid project configuration');
      }

      // Reset multi-manufacturing-method Results page bookkeeping — a new
      // project load starts with no active child selected; the Results page
      // will auto-pick the first Done child in its ngOnInit.
      this.activeResultsChildId = null;

      if (project.name) {
        this.project.name = project.name;
      }

      if (project.status) {
        this.project.status = project.status;
      }

      if (project.sharing) {
        this.sharing = project.sharing;
      }

      if (project.images) {
        Object.assign(this.projectImages, project.images);
      }

      const projectType = project.projectType || this.project.projectType;

      // For SoftJaws projects, only load minimal config
      if (projectType === ProjectType.SoftJaws) {
        if (this.project.status === 4) {
          this.softJawsResultUrl = project.optimization?.softjaws_result;
        }
        Object.assign(this.project, {
          flowVersion: project.config?.flowVersion,
          runType: project.config?.runType,
          projectType: projectType,
          partNames: project.config?.partNames || {},
          partVisibility: project.config?.partVisibility || {},
          softJaws: project.config?.softJaws || this.project.softJaws,
          unitSystem: project.config?.unitSystem ?? 'metric',
          partGroups: project.config?.partGroups ?? []
        });
      } else if (projectType === ProjectType.Mold) {
        // Deep-merge saved mold config with defaults so missing fields
        // (e.g. wall_thickness on older projects) get sensible values.
        const savedMold = project.config?.mold;
        const mergedMold = savedMold
          ? {
              ...DEFAULT_MOLD_CONFIG,
              ...savedMold,
              mold: { ...DEFAULT_MOLD_CONFIG.mold, ...savedMold.mold },
              ribs: {
                ...DEFAULT_MOLD_CONFIG.ribs,
                ...savedMold.ribs,
                manufacturing: { ...DEFAULT_MOLD_CONFIG.ribs.manufacturing, ...savedMold.ribs?.manufacturing },
                grids: savedMold.ribs?.grids ?? DEFAULT_MOLD_CONFIG.ribs.grids,
              },
              bosses: {
                ...DEFAULT_MOLD_CONFIG.bosses,
                ...savedMold.bosses,
                common: { ...DEFAULT_MOLD_CONFIG.bosses.common, ...savedMold.bosses?.common },
                list: savedMold.bosses?.list ?? DEFAULT_MOLD_CONFIG.bosses.list,
              },
            }
          : this.project.mold;
        Object.assign(this.project, {
          projectType: projectType,
          mold: mergedMold,
          partNames: project.config?.partNames || {},
          partTypes: project.config?.partTypes || this.project.partTypes,
          partVisibility: project.config?.partVisibility || {},
          unitSystem: project.config?.unitSystem ?? 'metric',
          partGroups: project.config?.partGroups ?? []
        });
      } else {
        // For Topology Optimization projects, load full config
        console.log('[ProjectConfig] setProject — topology optimization');
        console.log('[ProjectConfig] project.optimization present:', !!project.optimization);
        console.log('[ProjectConfig] project.config?.flowVersion:', project.config?.flowVersion);
        console.log('[ProjectConfig] project.config?.loadcases count:', project.config?.loadcases?.length);

        // Lift legacy single-method config into the new-shape manufacturingMethods[]
        // array if the backend payload doesn't already have it. Leaves the legacy
        // flat fields in place (V2 mirror strategy — see design doc §9).
        // Ensure manufacturingMethods[] exists even for brand-new projects
        // that have no config yet (backend returns config: null/{}).
        project.config = normalizeToNewShape(project.config ?? {} as any);

        Object.assign(this.project, {
          optimization: project.optimization || this.project.optimization,
          resolution: project.config?.resolution || this.project.resolution,
          symmetry: project.config?.symmetry || this.project.symmetry,
          manufacturingMethod: project.config?.manufacturingMethod || this.project.manufacturingMethod,
          manufacturingMethods: project.config?.manufacturingMethods || this.project.manufacturingMethods,
          // Per-method materials and per-variant goals are always on: the
          // Project Settings toggle is hidden and every project — including
          // ones saved before the flag existed, or with it explicitly off —
          // loads as advanced. The next save persists it.
          advancedSettings: true,
          staleSinceRun: project.config?.staleSinceRun ?? this.project.staleSinceRun ?? undefined,
          masterProjectId: project.masterProjectId ?? this.project.masterProjectId,
          methodId: project.methodId ?? this.project.methodId,
          childProjectIds: project.childProjectIds ?? this.project.childProjectIds,
          runSummary: project.runSummary ?? this.project.runSummary,
          millingVector: project.config?.millingVector || this.project.millingVector,
          drawDirectionVector: project.config?.drawDirectionVector || this.project.drawDirectionVector,
          ribsDensity: project.config?.ribsDensity || this.project.ribsDensity,
          drawDirectionIsMoldRemoval: project.config?.drawDirectionIsMoldRemoval === true,
          wallThickness: project.config?.wallThickness || this.project.wallThickness,
          bottomCutFrac: project.config?.bottomCutFrac || this.project.bottomCutFrac,
          materials: project.config?.materials?.map((item: any) => Material.fromJSON(item)) || this.project.materials,
          parts: {
            design: project.config?.parts?.design || this.project.parts.design,
            nondesign: project.config?.parts?.nondesign || this.project.parts.nondesign
          },
          partNames: project.config?.partNames || this.project.partNames,
          partMaterials: project.config?.partMaterials || this.project.partMaterials,
          partVisibility: project.config?.partVisibility || this.project.partVisibility,
          partGroups: project.config?.partGroups ?? [],
          loadcases:
            project.config?.loadcases?.map((item: any) => LoadCaseItem.fromJSON(item)) || this.project.loadcases,
          goal: {
            ...this.project.goal,
            fractionOfMaterial: project.config?.goal?.fractionOfMaterial || this.project.goal.fractionOfMaterial,
            massTarget: project.config?.goal?.massTarget || this.project.goal.massTarget,
            objective: project.config?.goal?.objective || this.project.goal.objective,
            frequencyConstraint: project.config?.goal?.frequencyConstraint || 200,
            designVolume: project.config?.goal?.designVolume,
            nonDesignVolume: project.config?.goal?.nonDesignVolume,
            partVolumes: project.config?.goal?.partVolumes,
            centerOfGravity: project.config?.goal?.centerOfGravity
          },
          minimumFeatureSize: project.config?.minimumFeatureSize || this.project.minimumFeatureSize,
          minimumToolSize: project.config?.minimumToolSize || this.project.minimumToolSize,
          minimumToolSizeUnit: normalizeToolSizeUnit(project.config?.minimumToolSizeUnit),
          userCoordinateSystem: project.config?.userCoordinateSystem || this.project.userCoordinateSystem,
          coordinateSystems: project.config?.coordinateSystems ?? [],
          generateOrganic: project.config?.generateOrganic ?? this.project.generateOrganic,
          generatePrismatic: project.config?.generatePrismatic ?? this.project.generatePrismatic,
          skeletonMethod: project.config?.skeletonMethod || this.project.skeletonMethod,
          stressConstraintEnabled:
            project.config?.stressConstraintEnabled ?? this.project.stressConstraintEnabled,
          deformationConstraintEnabled:
            project.config?.deformationConstraintEnabled ?? this.project.deformationConstraintEnabled,
          dynamicConstraintType:
            project.config?.dynamicConstraintType ?? this.project.dynamicConstraintType,
          flowVersion: project.config?.flowVersion || this.project.flowVersion,
          runType: project.config?.runType || this.project.runType,
          projectType: projectType,
          costModel: project.config?.costModel || this.project.costModel,
          numberOfPlanes: project.config?.numberOfPlanes ?? this.project.numberOfPlanes,
          extrusionPlaneGeneration:
            project.config?.extrusionPlaneGeneration ?? this.project.extrusionPlaneGeneration,
          maxMillingDepth: project.config?.manufacturingMethod === ManufacturingMethod.Milling2 ? project.config?.maxMillingDepth : undefined,
          thinWeb: project.config?.manufacturingMethod === ManufacturingMethod.Milling2 ? project.config?.thinWeb : undefined,
          castingParametric: supportsCastingParametric(project.config?.manufacturingMethod) ? project.config?.castingParametric : undefined,
          solverParameters: project.config?.solverParameters ?? this.project.solverParameters,
          unitSystem: project.config?.unitSystem ?? 'metric',
          densityUnit: project.config?.densityUnit ?? 'kg/m³',
          massUnit: project.config?.massUnit ?? 'kg'
        });
        // Projects saved before the turn was hidden store their inputs in the turned frame.
        storeInputsInModelFrame(this.project);
      }

      try {
        this.downloadLinks = (project as any).download;
        this.bdfJob = (project as any).bdfJob ?? null;
        if (this.project_id) {
          this.autoOrientService?.seed(
            this.project_id,
            (project as { autoOrient?: AutoOrient | null }).autoOrient ?? null,
          );
        }
      } catch (e) {}
    } catch (e: any) {
      console.error('Error setting project:', e.message);
    }

    try {
      this.setTabTitle(this.project.name);
    } catch (e) {}
  }

  setTabTitle(name: string): void {
    const prefix = environment.PRODUCTION ? '' : `(${environment.ENV_LABEL}) `;
    document.title = name ? `${prefix}${name} - InfinitForm` : `${prefix}InfinitForm`;
  }

  // ─── Multi-manufacturing-method: case management ───

  /**
   * Read the current flat scratch-pad fields on `this.project` into a
   * FlatManufacturingFields snapshot — used by `commitActiveCaseToArray`.
   */
  private readFlatSnapshot(): FlatManufacturingFields {
    return {
      manufacturingMethod: this.project.manufacturingMethod,
      millingVector: { ...this.project.millingVector },
      drawDirectionVector: { ...this.project.drawDirectionVector },
      generatePrismatic: this.project.generatePrismatic,
      costModel: this.project.costModel ? { ...this.project.costModel } : undefined,
      minimumToolSize: this.project.minimumToolSize,
      minimumToolSizeUnit: this.project.minimumToolSizeUnit,
      ribsDensity: this.project.ribsDensity,
      wallThickness: this.project.wallThickness,
      bottomCutFrac: this.project.bottomCutFrac,
      skeletonMethod: this.project.skeletonMethod,
      maxMillingDepth: this.project.maxMillingDepth ? { ...this.project.maxMillingDepth } : undefined,
      thinWeb: this.project.thinWeb ? { ...this.project.thinWeb } : undefined,
      castingParametric: this.project.castingParametric,
    };
  }

  /**
   * Copy the current flat scratch-pad fields into `manufacturingMethods[activeIndex]`,
   * recomputing the case's label from the captured direction flags. Called
   * before switching active case and before saving the project.
   *
   * No-op if the project has no manufacturingMethods[] (legacy path) or the
   * active id doesn't resolve to a case.
   */
  commitActiveCaseToArray(activeMethodId: string | null): void {
    const cases = this.project.manufacturingMethods;
    if (!cases || cases.length === 0) return;
    const idx = activeMethodId
      ? cases.findIndex((c) => c.methodId === activeMethodId)
      : 0;
    if (idx < 0) return;

    const captured = captureActiveCaseFromFlat(cases[idx], this.readFlatSnapshot());
    captured.label = computeCaseLabel(captured);
    // Variant labels embed the case label; the variants array is shared with
    // the previous case object, so relabel in place.
    const nameById = new Map((this.project.materials ?? []).map((m) => [m.id, m.name ?? '']));
    for (const v of captured.variants ?? []) {
      v.label = computeVariantLabel(captured, nameById.get(v.materialId) ?? '');
    }
    cases[idx] = captured;
  }

  /**
   * Copy `manufacturingMethods[targetCase]` into the flat scratch-pad on
   * `this.project`, so the optimization sub-blocks immediately reflect the
   * selected case. Called after `activeMethodId` changes.
   */
  hydrateFlatFromCase(methodId: string | null): void {
    const cases = this.project.manufacturingMethods;
    if (!cases || cases.length === 0) return;
    const target = methodId
      ? cases.find((c) => c.methodId === methodId)
      : cases[0];
    if (!target) return;

    Object.assign(this.project, projectFlatFromCase(target));
  }

  /**
   * Add a new manufacturing case to the project. Returns the new case's
   * methodId, or null if the addition was rejected (case or run cap
   * max cases reached).
   *
   * The caller is responsible for also calling `commitActiveCaseToArray`
   * with the OLD active id before invoking this, and for updating the
   * SessionService.activeMethodId pointer to the returned id.
   */
  addCase(method: ManufacturingMethod, maxCases: number = MAX_MANUFACTURING_METHODS): string | null {
    if (!this.project.manufacturingMethods) {
      this.project.manufacturingMethods = [];
    }
    const cases = this.project.manufacturingMethods;
    if (cases.length >= maxCases) return null;
    if (countTotalVariants(cases) >= MAX_TOTAL_VARIANTS) return null;
    if (!canAddMethod(method, cases)) return null;

    const methodId = this.generateMethodId();
    const newCase = makeDefaultCase(methodId, method);
    const materialId = resolveModelMaterialId(
      this.project.materials,
      this.project.partMaterials,
      this.project.parts,
    );
    if (materialId) {
      newCase.variants = [makeDefaultVariant(methodId, materialId)];
    }
    cases.push(newCase);
    return methodId;
  }

  /**
   * Add a material variant to an existing method. Returns the new variantId,
   * or null if the addition was rejected (cap reached, duplicate material,
   * unknown method).
   */
  addVariant(methodId: string, materialId: string): string | null {
    const cases = this.project.manufacturingMethods ?? [];
    const target = cases.find((c) => c.methodId === methodId);
    if (!target) return null;

    const variants = target.variants ?? [];
    if (variants.length >= MAX_MATERIALS_PER_METHOD) return null;
    if (countTotalVariants(cases) >= MAX_TOTAL_VARIANTS) return null;
    if (variants.some((v) => v.materialId === materialId)) return null;

    const variantId = this.generateMethodId();
    target.variants = [...variants, makeDefaultVariant(variantId, materialId)];
    return variantId;
  }

  /** Remove a variant. Refuses to remove a method's last remaining variant. */
  removeVariant(methodId: string, variantId: string): boolean {
    const target = this.project.manufacturingMethods?.find((c) => c.methodId === methodId);
    const variants = target?.variants;
    if (!target || !variants || variants.length <= 1) return false;

    const idx = variants.findIndex((v) => v.variantId === variantId);
    if (idx < 0) return false;
    target.variants = variants.filter((_, i) => i !== idx);
    return true;
  }

  /**
   * Set (or clear) a variant's user-supplied name. An empty `name` removes
   * the field so the run falls back to its derived label.
   *
   * Names must be unique across the WHOLE project, not just within a case:
   * a variant's name becomes its child project's name (`<master> — <name>`),
   * so two variants sharing one — even under different methods — produce two
   * children that are indistinguishable in Results, Compare, the cabinet and
   * the PDF. Compared case-insensitively, since two names differing only in
   * case are just as confusable on screen.
   */
  renameVariant(methodId: string, variantId: string, name: string): RenameVariantResult {
    const target = this.project.manufacturingMethods?.find((c) => c.methodId === methodId);
    const variant = target?.variants?.find((v) => v.variantId === variantId);
    if (!variant) return { status: 'unknown-variant' };

    const next = name.trim();
    if (next === (variant.name ?? '')) return { status: 'unchanged' };

    if (next) {
      const taken = (this.project.manufacturingMethods ?? []).some((c) =>
        (c.variants ?? []).some(
          (v) =>
            v.variantId !== variantId &&
            (v.name ?? '').trim().toLowerCase() === next.toLowerCase(),
        ),
      );
      if (taken) return { status: 'duplicate', name: next };
    }

    if (next) variant.name = next;
    else delete variant.name;
    return { status: 'renamed' };
  }

  /**
   * Remove a case from the project. Refuses to delete the last remaining
   * case. Returns true if the removal happened.
   *
   * If the removed case was the active one, the caller is responsible for
   * picking a new active case (typically the first remaining, or null if the
   * list is now empty) and calling `hydrateFlatFromCase` to update the
   * scratch-pad. `SessionService.removeManufacturingCase` already does this.
   *
   * Empty result is allowed: new projects start with no manufacturing methods
   * (the user picks one before optimization is enabled — see canOptimize).
   */
  removeCase(methodId: string): boolean {
    const cases = this.project.manufacturingMethods;
    if (!cases || cases.length === 0) return false;
    const idx = cases.findIndex((c) => c.methodId === methodId);
    if (idx < 0) return false;
    cases.splice(idx, 1);
    return true;
  }

  private generateMethodId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Multi-manufacturing-method: which child's results are currently shown on
   * the Results page. Null when:
   *  - the project isn't a master (legacy single-project)
   *  - no child has been selected yet
   *  - the master has no finished children to show
   *
   * Set by `mergeChildOptimizationIntoMaster` below. Used by the Results page's
   * case list to highlight the active case.
   */
  public activeResultsChildId: string | null = null;

  /**
   * Fetch a single child project's full document (including the
   * `optimization` URL block and materials array) and merge it into
   * `this.project.optimization` + `this.project.materials`. Also sets
   * `activeResultsChildId` so the Results page knows which case is active.
   *
   * Pure transport — does NOT touch the 3D engine. Callers are responsible
   * for tearing down the old mesh and reloading the new one.
   *
   * Returns the child's backend document, or null on failure.
   */
  async mergeChildOptimizationIntoMaster(
    childId: string,
  ): Promise<Record<string, unknown> | null> {
    const base = this.other_user_id ? `?other_user_id=${this.other_user_id}` : '';
    try {
      const childDoc = await lastValueFrom(
        this.http.get<Record<string, unknown>>(
          `${environment.API_ADDRESS}/project/${childId}${base}`,
        ),
      );
      if (!childDoc) return null;

      this.autoOrientService?.seed(
        childId,
        (childDoc as { autoOrient?: AutoOrient | null }).autoOrient ?? null,
      );

      const optimization = (childDoc as { optimization?: unknown }).optimization;
      if (optimization) {
        (this.project as { optimization?: unknown }).optimization = optimization;
      }

      // Propagate the child's flowVersion so the v2 results code path
      // (PLY + JSON loadcases) activates. The master's own flowVersion
      // stays at 1 because it never ran — the child has the real value.
      const childFlowVersion = (childDoc as { config?: { flowVersion?: number } })
        .config?.flowVersion;
      if (childFlowVersion && childFlowVersion > (this.project.flowVersion ?? 0)) {
        this.project.flowVersion = childFlowVersion;
      }

      // Child inherits materials from master at spawn time, but in case a
      // material override ever lands per-child, prefer the child's copy.
      const childConfig = (childDoc as { config?: { materials?: unknown[] } })
        .config;
      if (childConfig?.materials && Array.isArray(childConfig.materials)) {
        (this.project as { materials?: unknown }).materials =
          childConfig.materials.map((item: unknown) =>
            Material.fromJSON(item),
          );
      }

      this.activeResultsChildId = childId;
      return childDoc;
    } catch (err) {
      console.error(
        `[ProjectConfig] Failed to merge child optimization for ${childId}:`,
        err,
      );
      return null;
    }
  }

  /**
   * Fetch a lightweight summary of every child project for the currently
   * loaded master — one `GET /project/:id` per id listed in
   * `this.project.childProjectIds`, in parallel.
   *
   * Returns a minimal shape `{id, methodId, label, status, status_progress, image}`
   * per child. Used by the Compare View to populate tiles before the modal
   * does its own richer per-child fetch for mesh URLs + material density.
   *
   * Returns an empty array when the project has no children.
   */
  /**
   * Per-case detail for the assistant: material and result-geometry URLs for
   * every child of a multi-manufacturing master.
   *
   * The agent cannot read a child project itself — `GET /project/:id` admits
   * only an admin or the owning user, and the agent authenticates as a system
   * principal that is neither. Rather than widen that (which would hand the
   * agent read access far beyond the current user's own data), the GUI passes
   * across what it can already legitimately read with the user's own token.
   *
   * Cached briefly because the geometry URLs are presigned and every chat
   * message would otherwise re-fetch each case.
   */
  private caseDetailsCache: {
    masterId: string;
    fetchedAt: number;
    cases: ManufacturingCaseDetail[];
  } | null = null;

  async fetchMasterCaseDetails(): Promise<ManufacturingCaseDetail[]> {
    const masterId = this.project_id;
    const childIds = (this.project as { childProjectIds?: string[] }).childProjectIds;
    if (!masterId || !childIds || childIds.length < 2) return [];

    const cached = this.caseDetailsCache;
    if (
      cached &&
      cached.masterId === masterId &&
      Date.now() - cached.fetchedAt < CASE_DETAIL_TTL_MS
    ) {
      return cached.cases;
    }

    const labelByMethodId = new Map<string, string>();
    for (const c of (this.project as {
      manufacturingMethods?: Array<{ methodId: string; method: string; label?: string }>;
    }).manufacturingMethods ?? []) {
      labelByMethodId.set(c.methodId, c.label || c.method);
    }

    const base = this.other_user_id ? `?other_user_id=${this.other_user_id}` : '';
    const cases: ManufacturingCaseDetail[] = [];
    for (const childId of childIds) {
      try {
        const doc = await lastValueFrom(
          this.http.get<Record<string, any>>(
            `${environment.API_ADDRESS}/project/${childId}${base}`
          )
        );
        if (!doc) continue;
        const config = (doc['config'] ?? {}) as Record<string, any>;
        const material = (config['materials'] ?? [])[0];
        const v2 = ((doc['optimization'] ?? {})['v2'] ?? {}) as Record<string, any>;
        cases.push({
          id: childId,
          methodId: doc['methodId'] ?? null,
          label: labelByMethodId.get(doc['methodId']) ?? doc['name'] ?? null,
          status: doc['status'] ?? null,
          material: material
            ? {
                name: material['name'] ?? null,
                density: material['density'] ?? null,
                pricePerKg: material['price_per_kg'] ?? material['pricePerKg'] ?? null
              }
            : null,
          geometry: {
            organic: v2['organic']?.['geometryUrl'] ?? null,
            prismatic: v2['prismatic']?.['geometryUrl'] ?? null
          }
        });
      } catch {
        // A case we cannot read is simply absent; the assistant reports which
        // cases it received and the user is told what is missing.
      }
    }

    this.caseDetailsCache = { masterId, fetchedAt: Date.now(), cases };
    return cases;
  }

  async fetchMasterChildren(): Promise<
    Array<{
      id: string;
      methodId: string | null;
      label: string;
      status: number;
      status_progress: number;
      image: string | null;
      /**
       * The child's own geometry-metrics sidecar — a presigned URL on cloud, a
       * local path on desktop. Null when its run produced none. The process
       * comparison needs one per case: costing every route from a single
       * geometry is a different, weaker comparison.
       */
      geometryMetrics: string | null;
      /** The alloy this case ran against, which can differ per case. */
      caseMaterialName: string | null;
      /**
       * That alloy's density as the project stores it, in kg/m³. The costing
       * table prices only a few alloys, and its fallback density reports a
       * steel case at about a third of its real mass.
       */
      caseMaterialDensity: number | null;
      /**
       * Resolved from the master's variant catalog by the caller, not here —
       * declared so the result is a `CompareChild` rather than a cast.
       */
      materialId: string | null;
      materialName: string | null;
    }>
  > {
    const childIds = (this.project as { childProjectIds?: string[] })
      .childProjectIds;
    if (!childIds || childIds.length === 0) return [];

    // Resolve labels from the master's canonical manufacturingMethods[] array.
    // Children hold a variantId (not a case's own methodId) in their
    // `methodId` field, so the map must be keyed by variantId — see
    // buildVariantLabelMap.
    const cases = this.project.manufacturingMethods ?? [];
    const labelByMethodId = buildVariantLabelMap(cases);

    const base = this.other_user_id ? `?other_user_id=${this.other_user_id}` : '';
    const docs = await Promise.all(
      childIds.map((id) =>
        lastValueFrom(
          this.http.get<any>(`${environment.API_ADDRESS}/project/${id}${base}`),
        ).then((doc) => doc ? { ...doc, _childId: id } : null)
         .catch(() => null),
      ),
    );

    return docs
      .filter((doc): doc is Record<string, unknown> => !!doc)
      .map((doc) => {
        const methodId =
          typeof (doc as { methodId?: unknown }).methodId === 'string'
            ? ((doc as { methodId: string }).methodId)
            : null;
        const v2 = (
          doc as { optimization?: { v2?: Record<string, unknown> } }
        ).optimization?.v2;
        const metrics = v2?.['geometryMetrics'];
        // The cloud projection keeps materials under config; desktop
        // documents carry the array at the top level.
        type PlatformMaterial = { name?: unknown; density?: unknown };
        const materials =
          (doc as { config?: { materials?: PlatformMaterial[] } }).config?.materials ??
          (doc as { materials?: PlatformMaterial[] }).materials;
        return {
          // Cloud findOneForGUI doesn't return id/_id — use the child ID
          // we requested with. Desktop NeDB docs do return id.
          id: String(
            (doc as { id?: unknown }).id ??
            (doc as { _id?: unknown })._id ??
            (doc as { _childId?: unknown })._childId,
          ),
          methodId,
          label:
            (methodId && labelByMethodId.get(methodId)) ??
            ((doc as { name?: string }).name ?? ''),
          status: Number((doc as { status?: unknown }).status ?? 0),
          status_progress: Number((doc as { status_progress?: unknown }).status_progress ?? 0),
          image:
            typeof (doc as { image?: unknown }).image === 'string'
              ? ((doc as { image: string }).image)
              : null,
          geometryMetrics:
            v2?.['geometryMetricsExists'] === true &&
            typeof metrics === 'string' &&
            metrics !== ''
              ? metrics
              : null,
          caseMaterialName:
            typeof materials?.[0]?.name === 'string' ? (materials[0].name as string) : null,
          caseMaterialDensity:
            typeof materials?.[0]?.density === 'number'
              ? (materials[0].density as number)
              : null,
          materialId: null,
          materialName: null,
        };
      });
  }

  async getProject(): Promise<any> {
    // Keyed on project_id: a session can move between projects (a child id
    // redirects to its master), and an unkeyed cache would hand the new id the
    // previous project's document.
    if (this.projectLoaded && this.cachedProjectData && this.cachedProjectId === this.project_id) {
      return this.cachedProjectData;
    }

    this.progressService.updateProgress(10, 'Loading project data...');

    let url = `${environment.API_ADDRESS}/project/${this.project_id}`;
    if (this.other_user_id) url += `?other_user_id=${this.other_user_id}`;

    const result: any = await lastValueFrom(this.http.get(url));
    this.progressService.updateProgress(15, 'Project data loaded');

    // Cache the project data
    this.cachedProjectData = result;
    this.cachedProjectId = this.project_id;
    this.projectLoaded = true;

    // Set example project flag
    this.isExampleProject = result?.isExample === true;

    return result;
  }

  async copyExampleProject(): Promise<{ message: string } | null> {
    if (!this.project_id || !this.isExampleProject || this.copyingExampleProject) {
      return null;
    }

    this.copyingExampleProject = true;

    try {
      const result = await lastValueFrom(
        this.http.post<{ message: string }>(`${environment.API_ADDRESS}/example-projects/${this.project_id}/copy`, {})
      );
      return result;
    } catch (error) {
      console.error('Error copying example project:', error);
      throw error;
    } finally {
      this.copyingExampleProject = false;
    }
  }
}
